import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { CalendarClock, Info } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useUpdateBatchExpiry } from "~/hooks/production";
import { useToast } from "~/hooks/use-toast";
import type { ProductionBatch } from "~/types/production";

interface EditExpiryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch: ProductionBatch;
  onSuccess: () => void;
}

export function EditExpiryDialog({
  open,
  onOpenChange,
  batch,
  onSuccess,
}: EditExpiryDialogProps) {
  const { toast } = useToast();
  const updateExpiryMutation = useUpdateBatchExpiry();
  const [expiryDate, setExpiryDate] = useState(batch.expiry_date);

  useEffect(() => {
    if (open) {
      setExpiryDate(batch.expiry_date);
    }
  }, [batch.expiry_date, open]);

  const isUnchanged = expiryDate === batch.expiry_date;
  const isBeforeStart = dayjs(expiryDate).isBefore(dayjs(batch.start_date), "day");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!expiryDate) {
      toast({
        title: "Error de validación",
        description: "La fecha de vencimiento es obligatoria",
        variant: "destructive",
      });
      return;
    }

    if (isBeforeStart) {
      toast({
        title: "Error de validación",
        description:
          "La fecha de vencimiento no puede ser anterior a la fecha de inicio del lote",
        variant: "destructive",
      });
      return;
    }

    updateExpiryMutation.mutate(
      { id: batch.id, expiryDate },
      {
        onSuccess: () => {
          toast({
            title: "Vencimiento actualizado",
            description: `El vencimiento del lote cambió a ${dayjs(expiryDate).format("DD/MM/YYYY")}`,
          });
          onSuccess();
        },
        onError: (error) => {
          toast({
            title: "Error",
            description:
              error instanceof Error
                ? error.message
                : "No se pudo actualizar el vencimiento",
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            Editar vencimiento
          </DialogTitle>
          <DialogDescription>
            Lote {batch.batch_number}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit_expiry_date">Fecha de Vencimiento</Label>
            <Input
              id="edit_expiry_date"
              type="date"
              className="h-9"
              value={expiryDate}
              min={batch.start_date}
              onChange={(e) => setExpiryDate(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Actual: {dayjs(batch.expiry_date).format("DD/MM/YYYY")}
            </p>
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-100/80 p-3 text-sm text-gray-700">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
            <span>
              El nuevo vencimiento también se aplicará a los pallets e inventario
              ya producidos de este lote.
            </span>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateExpiryMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={updateExpiryMutation.isPending || isUnchanged}
            >
              {updateExpiryMutation.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
